import { useEffect, useState } from 'react';
import { Download, Paperclip } from 'lucide-react';
import api from '../../api/axios';
import { Button } from '../ui/button';
import { downloadDocumentFile } from '../../services/documents';
import { parseContentDispositionFilename, saveBlobAs } from '../../utils/downloadHelpers';
import type { DocumentNode } from '../../types/documents';

export function EventAttachments({ eventId }: { eventId: number }) {
  const [files, setFiles] = useState<DocumentNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get('api/documents/nodes/', { params: { owner_type: 'appointment', owner_id: eventId, node_type: 'FILE' } })
      .then(({ data }) => { if (!cancelled) setFiles(Array.isArray(data) ? data : data.results || []); })
      .catch(() => { if (!cancelled) setError('Impossibile caricare gli allegati.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [eventId, revision]);

  const upload = async (file: File) => {
    setBusy(true);
    setError(null);
    const form = new FormData();
    form.append('owner_type', 'appointment');
    form.append('owner_id', String(eventId));
    form.append('node_type', 'FILE');
    form.append('name', file.name);
    form.append('file', file);
    try {
      await api.post('api/documents/nodes/', form);
      setRevision(value => value + 1);
    } catch {
      setError('Impossibile caricare il file. Verifica i permessi e i limiti di caricamento.');
    } finally {
      setBusy(false);
    }
  };

  const download = async (file: DocumentNode) => {
    setError(null);
    try {
      const response = await downloadDocumentFile(file.id);
      saveBlobAs(response.data, parseContentDispositionFilename(response.headers?.['content-disposition']) || file.name);
    } catch {
      setError('Impossibile scaricare il file.');
    }
  };

  return <section aria-label="Allegati evento" className="space-y-2 min-w-0">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <h3 className="text-xs font-medium text-[var(--hexa-text-secondary)]">Allegati</h3>
      <label className="inline-flex min-h-10 cursor-pointer items-center gap-2 rounded-md border border-[var(--hexa-border)] px-3 text-xs">
        <Paperclip className="h-3.5 w-3.5" />{busy ? 'Caricamento...' : 'Carica allegato'}
        <input type="file" aria-label="Carica allegato evento" disabled={busy} className="sr-only"
          onChange={event => { const file = event.target.files?.[0]; event.target.value = ''; if (file) upload(file); }} />
      </label>
    </div>
    <p className="text-[11px] text-[var(--hexa-text-muted)]">I file saranno visibili anche nel contatto collegato all’evento.</p>
    {error && <p role="alert" className="text-xs text-red-500">{error}</p>}
    {loading ? <p className="text-xs">Caricamento allegati...</p> : files.length === 0 ? <p className="text-xs text-[var(--hexa-text-muted)]">Nessun allegato.</p>
      : <ul className="space-y-1">{files.map(file => <li key={file.id} className="flex min-w-0 items-center gap-2 rounded-md bg-[var(--hexa-button-cancel)] p-2">
        <span className="min-w-0 flex-1 break-words [overflow-wrap:anywhere] text-xs">{file.name}</span>
        <Button size="icon" variant="ghost" className="h-10 w-10 shrink-0" aria-label={`Scarica ${file.name}`} onClick={() => download(file)}><Download className="h-4 w-4" /></Button>
      </li>)}</ul>}
  </section>;
}
